import { MappedEntity } from '@api/api/entity-schema';
import { TSDatum } from '@api/api/timeseries-schema';
import { getStartOfRangeHorizon, RangeHorizon } from '@chart';
import { dateToUTC } from '@helpers/utils/dates/dates';
import { getPriceStatus } from '@helpers/utils/numbers/numbers';
import { ChartPreviewDropdown } from '@terminal/modules/chart-preview-dropdown/ChartPreviewDropdown';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { priceChangeColors } from '@terminal/utils/common/utils';
import * as d3 from 'd3';
import { useMemo } from 'react';

import styles from './ScreenerTableChart.module.scss';

export interface ScreenerTableChartProps {
  entity: MappedEntity;
  data: TSDatum[];
  width: number;
  height: number;
  horizon: RangeHorizon;
}

const CHART_PADDING_PX = 2;

export const ScreenerTableChart = ({
  entity,
  data,
  width,
  height,
  horizon,
}: ScreenerTableChartProps) => {
  const [plotLine, priceStatus] = useMemo(() => {
    const latestDataPoint = data[data.length - 1];
    const horizonStart = getStartOfRangeHorizon(
      horizon,
      new Date(latestDataPoint.index)
    );
    const normalizedHorizonStart =
      horizonStart === undefined
        ? undefined
        : dateToUTC(new Date(horizonStart)).getTime();
    const dataWithinHorizon =
      normalizedHorizonStart === undefined
        ? data
        : data.filter(
            item => new Date(item.index).getTime() >= normalizedHorizonStart
          );
    const [yMin = 0, yMax = 1] = d3.extent(
      dataWithinHorizon,
      item => item.value
    );

    const xScale = d3
      .scaleLinear()
      .domain([0, dataWithinHorizon.length - 1])
      .range([CHART_PADDING_PX, width - CHART_PADDING_PX]);

    const yScale = d3
      .scaleLinear()
      .domain([yMin, yMax])
      .range([height - CHART_PADDING_PX, CHART_PADDING_PX]);

    const plotLine = d3
      .line()
      .x(p => xScale(p[0]))
      .y(p => yScale(p[1]))
      .curve(d3.curveCardinal)(
      dataWithinHorizon.map((item, index) => [index, item.value])
    );
    const priceStatus = getPriceStatus(
      dataWithinHorizon[dataWithinHorizon.length - 1].value -
        dataWithinHorizon[0].value
    );

    return [plotLine, priceStatus];
  }, [data, width, height, horizon]);

  return (
    <ChartPreviewDropdown
      entity={entity}
      horizon={horizon}
      source={POSTHOG_TRACKING_SOURCES.SCREENER}
    >
      <svg data-testid="screener-table-chart" width={width} height={height}>
        <path
          className={styles.plotPath}
          data-testid="chart-path"
          style={{ stroke: priceChangeColors[priceStatus] }}
          d={plotLine ?? ''}
          strokeLinecap="round"
        />
      </svg>
    </ChartPreviewDropdown>
  );
};
